import { z } from 'zod';
import { FileIdSchema } from '../ids.ts';

export const BashInputSchema = z.object({
  command: z.string().optional(),
  restart: z.boolean().optional(),
});
export type BashInput = z.infer<typeof BashInputSchema>;

export const TextEditorInputSchema = z.object({
  command: z.enum(['view', 'create', 'str_replace', 'insert']),
  path: z.string(),
  // view: [start, end], 1-indexed, -1 means end of file.
  view_range: z.array(z.number().int()).optional(),
  file_text: z.string().optional(),
  old_str: z.string().optional(),
  new_str: z.string().optional(),
  // insert: 0 means the top of the file.
  insert_line: z.number().int().optional(),
  insert_text: z.string().optional(),
});
export type TextEditorInput = z.infer<typeof TextEditorInputSchema>;

export const BashOutputFileSchema = z.object({
  type: z.literal('bash_code_execution_output'),
  file_id: FileIdSchema,
  filename: z.string(),
  media_type: z.string(),
  size_bytes: z.number().int().nonnegative(),
});
export type BashOutputFile = z.infer<typeof BashOutputFileSchema>;

export const BASH_ERROR_CODES = [
  'unavailable',
  'execution_time_exceeded',
  'invalid_tool_input',
  'too_many_requests',
  'output_file_too_large',
] as const;

export const BashOutputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('bash_code_execution_result'),
    stdout: z.string(),
    stderr: z.string(),
    return_code: z.number().int(),
    content: z.array(BashOutputFileSchema),
    duration_ms: z.number().nonnegative(),
    truncated: z
      .object({ stdout_bytes: z.number().int(), stderr_bytes: z.number().int() })
      .optional(),
    sandbox_reset: z.boolean().optional(),
    files_expired: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('bash_code_execution_tool_result_error'),
    error_code: z.enum(BASH_ERROR_CODES),
    message: z.string().optional(),
  }),
]);
export type BashOutput = z.infer<typeof BashOutputSchema>;

export const TextEditorOutputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text_editor_code_execution_view_result'),
    file_type: z.string(),
    content: z.string(),
    num_lines: z.number().int().nullable(),
    start_line: z.number().int().nullable(),
    total_lines: z.number().int().nullable(),
    sandbox_reset: z.boolean().optional(),
    files_expired: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('text_editor_code_execution_create_result'),
    is_file_update: z.boolean(),
    sandbox_reset: z.boolean().optional(),
    files_expired: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('text_editor_code_execution_str_replace_result'),
    old_start: z.number().int().nullable(),
    old_lines: z.number().int().nullable(),
    new_start: z.number().int().nullable(),
    new_lines: z.number().int().nullable(),
    lines: z.array(z.string()).nullable(),
    sandbox_reset: z.boolean().optional(),
    files_expired: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('text_editor_code_execution_tool_result_error'),
    error_code: z.string(),
    message: z.string().optional(),
  }),
]);
export type TextEditorOutput = z.infer<typeof TextEditorOutputSchema>;

export const ViewImageInputSchema = z.object({ path: z.string() });
export type ViewImageInput = z.infer<typeof ViewImageInputSchema>;

export const VIEW_IMAGE_MEDIA_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;
export type ViewImageMediaType = (typeof VIEW_IMAGE_MEDIA_TYPES)[number];

export const VIEW_IMAGE_ERROR_CODES = [
  'invalid_tool_input',
  'file_not_found',
  'unsupported_image',
  'image_too_large',
  'images_not_supported',
  'unavailable',
  'execution_time_exceeded',
] as const;
export type ViewImageErrorCode = (typeof VIEW_IMAGE_ERROR_CODES)[number];

export const ViewImageOutputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('view_image_result'),
    path: z.string(),
    media_type: z.enum(VIEW_IMAGE_MEDIA_TYPES),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    scaled_from: z
      .object({ width: z.number().int().positive(), height: z.number().int().positive() })
      .nullable(),
    bytes: z.number().int().nonnegative(),
    data: z.string(),
    sandbox_reset: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('view_image_error'),
    error_code: z.enum(VIEW_IMAGE_ERROR_CODES),
    message: z.string(),
  }),
]);
export type ViewImageOutput = z.infer<typeof ViewImageOutputSchema>;

export function viewImageOutputForBrowser(output: unknown): unknown {
  const parsed = ViewImageOutputSchema.safeParse(output);
  if (!parsed.success || parsed.data.type !== 'view_image_result') return output;
  return { ...parsed.data, data: '' };
}
